import type { ReactNode, RefObject } from "react";
import { PageHeader } from "@/components/PageHeader";
import { cn } from "@/lib/utils";

/**
 * The frame of every settings screen (spec `web-shell` "Settings screens use the shared
 * settings shell"): the page header, the notices every group shows, the controls row (a level
 * picker and the search, or the search alone), the group tabs, then the group shown. It renders
 * no `Page`: the caller's container sets the padding and the gap between these parts.
 */
export function SettingsShell(props: {
  title: ReactNode;
  lead?: ReactNode;
  /** Above the title: a breadcrumb. */
  before?: ReactNode;
  notices?: ReactNode;
  controls?: ReactNode;
  /** Where the controls row turns into two columns. */
  controlsFrom?: "sm" | "lg";
  tabs?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <>
      <PageHeader before={props.before} title={props.title} lead={props.lead} />
      {props.notices}
      {props.controls ? (
        <div
          className={cn(
            "grid gap-4",
            props.controlsFrom === "lg" ? "lg:grid-cols-2" : "sm:grid-cols-2",
          )}
        >
          {props.controls}
        </div>
      ) : null}
      {props.tabs}
      {props.children}
    </>
  );
}

/**
 * One group's page: its heading (focused on a group change, so `tabIndex=-1`), its purpose,
 * an optional summary and related links, then its sections.
 */
export function SettingsGroupPage(props: {
  id: string;
  heading: ReactNode;
  headingRef: RefObject<HTMLHeadingElement | null>;
  purpose: ReactNode;
  summary?: ReactNode;
  related?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section aria-labelledby={`group-${props.id}`} className="flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          <h2
            id={`group-${props.id}`}
            ref={props.headingRef}
            tabIndex={-1}
            className="text-base font-semibold text-fg outline-offset-4"
          >
            {props.heading}
          </h2>
          <p className="max-w-prose text-sm text-fg-secondary">{props.purpose}</p>
        </div>
        {props.summary}
        {props.related}
      </header>
      {props.children}
    </section>
  );
}

/** A section of a group page: its heading over the hairline container of its rows. */
export function SettingsSectionBlock(props: {
  id: string;
  heading: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section aria-labelledby={`section-${props.id}`} className="flex flex-col gap-2">
      <h3 id={`section-${props.id}`} className="text-sm font-semibold text-fg">
        {props.heading}
      </h3>
      <ul className="divide-y divide-line rounded-lg border border-line bg-surface-raised">
        {props.children}
      </ul>
    </section>
  );
}
